// Utils
import * as elementRegistry from '../../../../../common/elements/elementRegistry';
import platformSingleton from '../../../../platform/platformSingleton';
import { isPlatformIpad } from '../../../../platform/utils/platformDetailsUtils';
import { feedbackRequired } from '../toolbarToolConfigUtils';

// Components
import ElementToolContainer from '../../components/elementTools/ElementToolContainer';
import AiAssistantTool from '../../components/elementTools/aiAssistantTool/AiAssistantTool';
import FileTool from '../../components/elementTools/fileTool/FileTool';
import ImageTool from '../../components/elementTools/imageTool/ImageTool';
import DrawingTool from '../../components/elementTools/DrawingTool';
import LinkTypeTool from '../../components/elementTools/LinkTypeTool';
import HeaderTool from '../../components/elementTools/HeaderTool';
import ColorSwatchTool from '../../components/elementTools/ColorSwatchTool';
import TaskListTool from '../../components/elementTools/TaskListTool';
import BoardTool from '../../components/elementTools/BoardTool';

// Constants
import { COLLAPSE_ORDER, TOOL_HEIGHT } from '../toolbarToolConfigConstants';

import { LINK_ELEMENT_LINK_TYPES } from '../../../../../common/links/linkConstants';
import { AI_ASSISTANT_TOOL_TYPE } from '../../../../../common/ai/aiToolConstants';

// Types
import { ToolbarItemConfig } from '../../toolbarTypes';
import { ElementType } from '../../../../../common/elements/elementTypes';
import { MilanoteApplicationMode } from '../../../../../common/platform/platformTypes';

const elementTypes: { [key: string]: any } = elementRegistry.getElementTypeObjects();

const ELEMENT_CARD: ToolbarItemConfig = {
    id: ElementType.CARD_TYPE,
    name: 'Note',
    height: TOOL_HEIGHT.SHORT,
    collapseOrder: 100,
    component: ElementToolContainer,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.CARD_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ELEMENT_LINK: ToolbarItemConfig = {
    id: ElementType.LINK_TYPE,
    name: 'Link',
    height: TOOL_HEIGHT.SHORT,
    collapseOrder: 100,
    component: ElementToolContainer,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.LINK_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
        focusInputEditorType: 'url',
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ELEMENT_TASK_LIST: ToolbarItemConfig = {
    id: ElementType.TASK_LIST_TYPE,
    name: 'To-do',
    height: TOOL_HEIGHT.SHORT,
    collapseOrder: 100,
    component: TaskListTool,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.TASK_LIST_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ELEMENT_LINE: ToolbarItemConfig = {
    id: ElementType.LINE_TYPE,
    name: 'Line',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 100,
    component: ElementToolContainer,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.LINE_TYPE],
        editOnCreate: false,
        selectOnCreate: true,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ELEMENT_BOARD: ToolbarItemConfig = {
    id: ElementType.BOARD_TYPE,
    name: 'Board',
    height: TOOL_HEIGHT.SQUARE,
    // Collapse the board element later than other elements
    collapseOrder: COLLAPSE_ORDER.LATE,
    component: BoardTool,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.BOARD_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ELEMENT_COLUMN: ToolbarItemConfig = {
    id: ElementType.COLUMN_TYPE,
    name: 'Column',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 100,
    component: ElementToolContainer,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.COLUMN_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ELEMENT_COMMENT_THREAD: ToolbarItemConfig = {
    id: ElementType.COMMENT_THREAD_TYPE,
    name: 'Comment',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 100,
    component: ElementToolContainer,
    permissionPredicate: feedbackRequired,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.COMMENT_THREAD_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
};

const ELEMENT_TABLE: ToolbarItemConfig = {
    id: ElementType.TABLE_TYPE,
    name: 'Table',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 100,
    component: ElementToolContainer,
    appModeAvailability: [MilanoteApplicationMode.desktop],
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.TABLE_TYPE],
        editOnCreate: false,
        selectOnCreate: true,
    },
};

const ELEMENT_SKETCH: ToolbarItemConfig = {
    id: ElementType.SKETCH_TYPE,
    name: 'Sketch',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    component: ElementToolContainer,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.SKETCH_TYPE],
        editOnCreate: false,
        selectOnCreate: false,
    },
};

const ELEMENT_COLOR_SWATCH: ToolbarItemConfig = {
    id: ElementType.COLOR_SWATCH_TYPE,
    name: 'Color Swatch',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    component: ColorSwatchTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.COLOR_SWATCH_TYPE],
        editOnCreate: false,
        selectOnCreate: false,
    },
};

const ELEMENT_DOCUMENT: ToolbarItemConfig = {
    id: ElementType.DOCUMENT_TYPE,
    name: 'Document',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    component: ElementToolContainer,
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.DOCUMENT_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
};

const ELEMENT_LINK_AUDIO: ToolbarItemConfig = {
    id: `${ElementType.LINK_TYPE}-${LINK_ELEMENT_LINK_TYPES.AUDIO}`,
    name: 'Audio',
    height: TOOL_HEIGHT.SQUARE,
    component: LinkTypeTool,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    appModeAvailability: [MilanoteApplicationMode.desktop],
    toolSettings: {
        linkType: LINK_ELEMENT_LINK_TYPES.AUDIO,
        elementTypeObject: elementTypes[ElementType.LINK_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
};

const ELEMENT_LINK_MAP: ToolbarItemConfig = {
    id: `${ElementType.LINK_TYPE}-${LINK_ELEMENT_LINK_TYPES.MAP}`,
    name: 'Map',
    height: TOOL_HEIGHT.SQUARE,
    component: LinkTypeTool,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    appModeAvailability: [MilanoteApplicationMode.desktop],
    toolSettings: {
        linkType: LINK_ELEMENT_LINK_TYPES.MAP,
        elementTypeObject: elementTypes[ElementType.LINK_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
};

const ELEMENT_LINK_VIDEO: ToolbarItemConfig = {
    id: `${ElementType.LINK_TYPE}-${LINK_ELEMENT_LINK_TYPES.VIDEO}`,
    name: 'Video',
    height: TOOL_HEIGHT.SQUARE,
    component: LinkTypeTool,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    appModeAvailability: [MilanoteApplicationMode.desktop],
    toolSettings: {
        linkType: LINK_ELEMENT_LINK_TYPES.VIDEO,
        elementTypeObject: elementTypes[ElementType.LINK_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
};

const ELEMENT_CARD_HEADING: ToolbarItemConfig = {
    id: `${ElementType.CARD_TYPE}-HEADER`,
    name: 'Heading',
    height: TOOL_HEIGHT.SHORT,
    component: HeaderTool,
    collapseOrder: COLLAPSE_ORDER.ALWAYS,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    toolSettings: {
        elementTypeObject: elementTypes[ElementType.CARD_TYPE],
        editOnCreate: true,
        selectOnCreate: true,
    },
};

const TOOL_AI_ASSISTANT: ToolbarItemConfig = {
    id: AI_ASSISTANT_TOOL_TYPE,
    name: 'AI Ideas',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 2,
    collapseBehavior: 'hide',
    component: AiAssistantTool,
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const TOOL_IMAGE: ToolbarItemConfig = {
    id: ElementType.IMAGE_TYPE,
    iconName: 'toolbar-image',
    name: 'Add image',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 2,
    collapseBehavior: 'hide',
    component: ImageTool,
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const TOOL_UPLOAD_FILE: ToolbarItemConfig = {
    id: ElementType.FILE_TYPE,
    iconName: 'toolbar-file',
    name: 'Upload',
    height: TOOL_HEIGHT.SQUARE,
    /*
    On iPad, we never want to collapse the upload tool because uploading files is very difficult otherwise.
    On other devices set the collapse order to 2, which will keep it available until very few tools are left
    */
    collapseOrder: isPlatformIpad(platformSingleton) ? COLLAPSE_ORDER.NEVER : 2,
    collapseBehavior: 'hide',
    component: FileTool,
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const TOOL_ANNOTATE: ToolbarItemConfig = {
    id: ElementType.ANNOTATION_TYPE,
    name: 'Draw',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: 2,
    collapseBehavior: 'hide',
    permissionPredicate: feedbackRequired,
    component: DrawingTool,
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

export default {
    ELEMENT_CARD,
    ELEMENT_LINK,
    ELEMENT_TASK_LIST,
    ELEMENT_LINE,
    ELEMENT_BOARD,
    ELEMENT_COLUMN,
    ELEMENT_COMMENT_THREAD,
    ELEMENT_TABLE,
    ELEMENT_SKETCH,
    ELEMENT_COLOR_SWATCH,
    ELEMENT_DOCUMENT,
    ELEMENT_LINK_AUDIO,
    ELEMENT_LINK_MAP,
    ELEMENT_LINK_VIDEO,
    ELEMENT_CARD_HEADING,
    TOOL_AI_ASSISTANT,
    TOOL_IMAGE,
    TOOL_UPLOAD_FILE,
    TOOL_ANNOTATE,
} as const;
